import { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useUserPreferences } from '../context/UserPreferencesContext';
import { TOGGLEABLE_PAGES } from '../components/PageNavList';
import useAiStatus from '../hooks/useAiStatus';
import {
  BOARD_GROUP_OPTIONS,
  MIN_BOARD_DAYS,
  MAX_BOARD_DAYS,
  clampBoardDays,
  getBoardGroupBy,
  getBoardDayCount,
} from './BoardPage';
import API from '../api/axios';

export default function SettingsPage() {
  const { theme, toggleTheme, compactFont, toggleCompactFont } = useTheme();
  const { preferences, hiddenPages, togglePageHidden, savePreferences } = useUserPreferences();
  const { aiEnabled } = useAiStatus();
  // Focusly AI has no toggle where the deployment cannot run it.
  const toggleablePages = TOGGLEABLE_PAGES.filter((p) => p.path !== '/ai' || aiEnabled);
  const eventsEnabled = preferences.plannerEventsEnabled !== false;
  const boardGroupBy = getBoardGroupBy(preferences);
  const boardDayCount = getBoardDayCount(preferences);

  // The day count is free-typed, so it is held locally while the field is being
  // edited (it can legitimately be empty mid-keystroke) and only clamped and
  // persisted on commit.
  const [dayCountDraft, setDayCountDraft] = useState(String(boardDayCount));
  useEffect(() => { setDayCountDraft(String(boardDayCount)); }, [boardDayCount]);

  const commitDayCount = () => {
    const next = clampBoardDays(dayCountDraft);
    setDayCountDraft(String(next));
    if (next !== boardDayCount) {
      savePreferences({ ...preferences, boardDayCount: next });
    }
  };

  const [tags, setTags] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editTag, setEditTag] = useState(null);
  const [form, setForm] = useState({ name: '', color: '#6c757d' });
  const [resetting, setResetting] = useState(false);

  const fetchTags = async () => {
    const { data } = await API.get('/tags');
    setTags(data);
  };

  useEffect(() => { fetchTags(); }, []);

  const openCreate = () => {
    setEditTag(null);
    setForm({ name: '', color: '#6c757d' });
    setShowForm(true);
  };

  const openEdit = (tag) => {
    setEditTag(tag);
    setForm({ name: tag.name, color: tag.color });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this tag?')) return;
    await API.delete(`/tags/${id}`);
    fetchTags();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    if (editTag) {
      await API.put(`/tags/${editTag.id}`, form);
    } else {
      await API.post('/tags', form);
    }
    setShowForm(false);
    fetchTags();
  };

  const handleResetData = async () => {
    if (!window.confirm('Are you sure you want to delete ALL your data? This will permanently delete all tasks, folders, and tags. This action cannot be undone.')) return;
    if (!window.confirm('This is your final warning. All data will be permanently deleted. Continue?')) return;

    setResetting(true);
    try {
      await API.delete('/settings/reset-data');
      setTags([]);
      alert('All data has been reset successfully.');
    } catch (err) {
      alert('Failed to reset data. Please try again.');
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="container py-4" style={{ maxWidth: 600 }}>
      <h4 className="mb-4">Settings</h4>

      <div className="card mb-4">
        <div className="card-body">
          <h5 className="card-title">Appearance</h5>

          <div className="d-flex justify-content-between align-items-center">
            <div>
              <div className="fw-medium">Dark Mode</div>
              <small className="text-muted">
                Toggle between light and dark theme
              </small>
            </div>
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id="darkModeToggle"
                checked={theme === 'dark'}
                onChange={toggleTheme}
                style={{ width: '3em', height: '1.5em' }}
              />
              <label className="form-check-label visually-hidden" htmlFor="darkModeToggle">
                Dark Mode
              </label>
            </div>
          </div>

          <hr />

          <div className="d-flex justify-content-between align-items-center">
            <div>
              <div className="fw-medium">Small Fonts</div>
              <small className="text-muted">
                Use a smaller font size across the app
              </small>
            </div>
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id="compactFontToggle"
                checked={compactFont}
                onChange={toggleCompactFont}
                style={{ width: '3em', height: '1.5em' }}
              />
              <label className="form-check-label visually-hidden" htmlFor="compactFontToggle">
                Small Fonts
              </label>
            </div>
          </div>
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-body">
          <h5 className="card-title">Daily</h5>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <div className="fw-medium">Events</div>
              <small className="text-muted">
                Show the hourly schedule on the Daily page
              </small>
            </div>
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id="plannerEventsToggle"
                checked={eventsEnabled}
                onChange={() => savePreferences({ ...preferences, plannerEventsEnabled: !eventsEnabled })}
                style={{ width: '3em', height: '1.5em' }}
              />
              <label className="form-check-label visually-hidden" htmlFor="plannerEventsToggle">
                Planner Events
              </label>
            </div>
          </div>
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-body">
          <h5 className="card-title">Board</h5>

          <div className="d-flex justify-content-between align-items-center gap-3">
            <div>
              <div className="fw-medium">Group By</div>
              <small className="text-muted">
                What the columns on the Board page represent
              </small>
            </div>
            <select
              className="form-select"
              style={{ width: 'auto' }}
              id="boardGroupBy"
              aria-label="Board grouping"
              value={boardGroupBy}
              onChange={(e) => savePreferences({ ...preferences, boardGroupBy: e.target.value })}
            >
              {BOARD_GROUP_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {boardGroupBy === 'dueDate' && (
            <>
              <hr />
              <div className="d-flex justify-content-between align-items-center gap-3">
                <div>
                  <div className="fw-medium">Days Shown</div>
                  <small className="text-muted">
                    How many day columns the board covers, starting from today.
                    Overdue and undated tasks get their own columns.
                  </small>
                </div>
                <input
                  type="number"
                  className="form-control"
                  style={{ width: 90 }}
                  id="boardDayCount"
                  aria-label="Days shown on the board"
                  min={MIN_BOARD_DAYS}
                  max={MAX_BOARD_DAYS}
                  value={dayCountDraft}
                  onChange={(e) => setDayCountDraft(e.target.value)}
                  onBlur={commitDayCount}
                  onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                />
              </div>
            </>
          )}
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-body">
          <h5 className="card-title">Pages</h5>
          <p className="text-muted small mb-3">
            Choose which pages appear in the sidebar. Settings is always shown.
          </p>
          {toggleablePages.map((page, idx) => {
            const visible = !hiddenPages.includes(page.path);
            const id = `page-toggle-${page.path.slice(1)}`;
            return (
              <div key={page.path}>
                {idx > 0 && <hr />}
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <div className="fw-medium">
                      <span className="me-2">{page.icon}</span>
                      {page.name}
                    </div>
                  </div>
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id={id}
                      checked={visible}
                      onChange={() => togglePageHidden(page.path)}
                      style={{ width: '3em', height: '1.5em' }}
                    />
                    <label className="form-check-label visually-hidden" htmlFor={id}>
                      {page.name}
                    </label>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="card-title mb-0">Tags</h5>
            <button className="btn btn-primary btn-sm" onClick={openCreate}>+ Add Tag</button>
          </div>

          {showForm && (
            <form className="card card-body mb-3" onSubmit={handleSubmit}>
              <div className="d-flex gap-2 align-items-end">
                <div className="flex-grow-1">
                  <label className="form-label">Name</label>
                  <input
                    className="form-control"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    autoFocus
                    required
                  />
                </div>
                <div>
                  <label className="form-label">Color</label>
                  <input
                    type="color"
                    className="form-control form-control-color"
                    value={form.color}
                    onChange={(e) => setForm({ ...form, color: e.target.value })}
                  />
                </div>
                <button type="submit" className="btn btn-primary" disabled={!form.name.trim()}>
                  {editTag ? 'Update' : 'Create'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>
                  Cancel
                </button>
              </div>
            </form>
          )}

          {tags.length === 0 ? (
            <p className="text-muted mb-0">No tags yet. Create one to get started.</p>
          ) : (
            <ul className="list-group">
              {tags.map((tag) => (
                <li key={tag.id} className="list-group-item d-flex align-items-center">
                  <span
                    className="rounded-circle me-2"
                    style={{ width: 16, height: 16, backgroundColor: tag.color, display: 'inline-block' }}
                  />
                  <span className="flex-grow-1">{tag.name}</span>
                  <button className="btn btn-sm btn-outline-secondary me-1" onClick={() => openEdit(tag)}>Edit</button>
                  <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(tag.id)}>Delete</button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="card mt-4">
        <div className="card-body">
          <h5 className="card-title text-danger">Danger Zone</h5>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <div className="fw-medium">Reset Data</div>
              <small className="text-muted">
                Permanently delete all tasks, folders, and tags
              </small>
            </div>
            <button
              className="btn btn-danger"
              onClick={handleResetData}
              disabled={resetting}
            >
              {resetting ? 'Resetting...' : 'Reset All Data'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
